import { useEffect, useState } from 'react';
import { AlertCircle } from '../../ui/icons';
import Colors from '../../../constants/Colors';
import { gamesApi, Game, OpsTipoffTaskLogEntry } from '../../../services/api';
import { useMqttFeed, MqttStatusBar } from './MqttFeed';

/** Extracts the ECS task id from an ARN like
 * "arn:aws:ecs:us-east-1:989061864277:task/dev-mqtt-data-cluster/<task-id>". */
function taskIdFromArn(arn?: string | null): string | null {
  if (!arn) return null;
  const parts = arn.split('/');
  const id = parts[parts.length - 1];
  return id || null;
}

interface LiveTipoffMqttProps {
  gameName: string;
  direction: 'home' | 'away';
  /** Called for each incoming message, normalized to the same shape as the historical
   * CloudWatch task-log entries, so the caller can merge live + historical into one list. */
  onMessage: (entry: OpsTipoffTaskLogEntry) => void;
}

export default function LiveTipoffMqtt({ gameName, direction, onMessage }: LiveTipoffMqttProps) {
  const [game, setGame] = useState<Game | null>(null);
  const [gameLoading, setGameLoading] = useState(false);
  const [gameError, setGameError] = useState<string | null>(null);

  useEffect(() => {
    if (!gameName) return;
    let cancelled = false;
    setGameLoading(true);
    (async () => {
      // Not every card has a matching Step Functions orchestration (e.g. audio/tipoff tasks
      // launched manually bypass it entirely), so look the game up by name - always available
      // from the ECS task tags - rather than depending on an orchestration's game_id.
      const response = await gamesApi.listGames({ search: gameName });
      if (cancelled) return;
      const match = response.data?.games?.find((g) => g.game_name === gameName) || null;
      if (response.success && match) {
        setGameError(null);
        setGame(match);
      } else {
        setGameError(response.error || response.data?.error || `No game record found for "${gameName}"`);
        setGame(null);
      }
      setGameLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [gameName]);

  const taskArn = direction === 'home'
    ? game?.home_radioTipoffTaskArn || game?.radioTipoffTaskArn
    : game?.away_radioTipoffTaskArn;
  const taskId = taskIdFromArn(taskArn);
  const topic = taskId ? `tipoff-detector/radio-tipoff/${taskId}` : null;

  const { status, error: connectionError, connect, disconnect } = useMqttFeed(topic, (raw) => {
    let timestamp = Date.now();
    let message = raw;
    try {
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === 'object') {
        if (typeof parsed.timestamp === 'number') timestamp = parsed.timestamp;
        if (typeof parsed.message === 'string') message = parsed.message;
      }
    } catch {
      // Not JSON - fall back to the raw payload text as the message.
    }
    // Match the CloudWatch entries' microsecond-looking suffix (they use 6 fractional
    // digits) purely for visual consistency between the two sources in one merged list.
    const timestampIso = new Date(timestamp).toISOString().replace(/(\.\d{3})Z$/, '$1000Z');
    onMessage({ timestamp, timestamp_iso: timestampIso, message });
  });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
      <MqttStatusBar
        status={status}
        topicLabel={topic}
        onConnect={connect}
        onDisconnect={disconnect}
        connectDisabled={!taskId}
        connectLoading={gameLoading}
      />

      {gameError && (
        <div className="alert alert-warning" style={{ margin: 0 }}>
          <AlertCircle size={18} />
          <div>{gameError}</div>
        </div>
      )}

      {!gameError && !gameLoading && game && !taskId && (
        <div style={{ fontSize: '12.5px', color: Colors.textLight }}>
          No radio-tipoff task ARN on this game record yet — nothing to subscribe to.
        </div>
      )}

      {connectionError && (
        <div className="alert alert-warning" style={{ margin: 0 }}>
          <AlertCircle size={18} />
          <div>{connectionError}</div>
        </div>
      )}
    </div>
  );
}
